import React from 'react';
import { StyledForm } from '@Components/shared/Form.styles';
import {
	Grid,
	FormControl,
	InputLabel,
	Select,
	FormHelperText,
	Autocomplete,
	SelectChangeEvent,
	TextField,
	MenuItem,
} from '@mui/material';
import { useGlobalization } from '@Translations';
import { SystemConnectionType } from './NewSystemConnectionForm';
import { SECURITY_POLICIES_DEFAULT } from './OpcuaTypes';
import { VisiblePasswordField } from './VisiblePassword';

export interface OpcUaFieldsProps {
	updateFormData: (p: Record<string, string>, fromUser?: boolean) => void;
	formData: Record<string, string>;
	formErrors: Record<string, string>;
	refreshOPCUAUrl: () => void;
	tzOptions: { key: string; value: string }[];
	tzDefault: { key: string; value: string } | undefined;
	systemConnectionTypes: SystemConnectionType[];
	handleConnectionTypeChange: (event: SelectChangeEvent<string>) => void;
	securityModes: { label: string; value: string }[];
	securityPolicies: { label: string; value: string }[];
}

export const OpcUaFields = (props: OpcUaFieldsProps) => {
	const { t } = useGlobalization();

	const handleSecurityModeChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		const fieldName = target?.name;
		const value = target?.value;
		const securityPolicy =
			value === 'None' ? 'None' : SECURITY_POLICIES_DEFAULT;
		props.updateFormData({ [fieldName]: value, securityPolicy }, true);
	};

	const handleSelectChange = (
		//NOSONAR
		event: SelectChangeEvent<string>,
	) => {
		const target = event?.target as HTMLInputElement;
		const fieldName = target?.name;
		if (fieldName === 'securityMode') handleSecurityModeChange(event);
		else props.updateFormData({ [fieldName]: target?.value }, true);
	};

	const textFieldChanged = (
		//NOSONAR
		event:
			| KeyboardEvent
			| React.ChangeEvent<
					HTMLInputElement | HTMLTextAreaElement | unknown
			  >,
	) => {
		const target = event?.target as HTMLInputElement;
		// special case for username and password to remove the placeholder dots
		if (target?.name === 'username' || target?.name === 'password') {
			target.value = target.value.replaceAll('•', '');
		}
		props.updateFormData(
			{
				[target?.name]: target?.value || '',
			},
			true,
		);
	};

	const numberFieldChanged = (
		event:
			| KeyboardEvent
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const target = event?.target as HTMLInputElement;
		let targetValue: string;
		try {
			targetValue = `${parseInt(target?.value)}`;
		} catch {
			targetValue = target?.value;
		}
		props.updateFormData(
			{
				[target?.name]: targetValue || '0',
			},
			true,
		);
	};

	const changeAutocompleteHandler = (name: string) => {
		return (
			event: React.ChangeEvent<unknown>,
			newValue: { key: unknown; value: string } | null,
		) => {
			props.updateFormData(
				{ [name]: (newValue?.key as never) || '' },
				true,
			);
		};
	};

	const ignoreScroll = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	return (
		<StyledForm autoComplete="off">
			<Grid container spacing={3}>
				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						required={true}
						label={t('Connection Name')}
						variant="outlined"
						name={'connectionName'}
						value={props.formData?.connectionName ?? ''}
						onChange={textFieldChanged}
						error={!!props.formErrors?.connectionName}
						helperText={props.formErrors?.connectionName}
					/>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						error={!!props.formErrors?.connectionType}>
						<InputLabel>{t('Connection Type *')}</InputLabel>
						<Select
							name="connectionType"
							required={true}
							value={props.formData?.connectionType ?? 'ODBC'}
							onChange={props.handleConnectionTypeChange}
							error={!!props.formErrors?.connectionType}
							label={t('Connection Type')}>
							{props.systemConnectionTypes.map(
								({
									systemConnectionTypeName,
									systemConnectionTypeId,
								}) => (
									<MenuItem
										key={systemConnectionTypeId}
										value={systemConnectionTypeId}>
										{systemConnectionTypeName}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.connectionType && (
							<FormHelperText>
								{props.formErrors?.connectionType}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Server Address')}
						required={true}
						variant="outlined"
						name={'serverAddress'}
						value={props.formData?.serverAddress ?? ''}
						onChange={textFieldChanged}
						onBlur={props.refreshOPCUAUrl}
						error={!!props.formErrors?.serverAddress}
						helperText={props.formErrors?.serverAddress}
					/>
				</Grid>

				<Grid item sm={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Port')}
						required={true}
						variant="outlined"
						name={'port'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.port ?? ''}
						onChange={numberFieldChanged}
						onBlur={props.refreshOPCUAUrl}
						error={!!props.formErrors?.port}
						helperText={props.formErrors?.port}
					/>
				</Grid>

				<Grid item xs={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Resource Path')}
						required={false}
						variant="outlined"
						name={'resourcePath'}
						onChange={textFieldChanged}
						onBlur={props.refreshOPCUAUrl}
						value={props.formData?.resourcePath ?? ''}
						error={!!props.formErrors?.resourcePath}
						helperText={props.formErrors?.resourcePath}
					/>
				</Grid>

				<Grid item xs={4}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Username')}
						required={true}
						variant="outlined"
						name={'username'}
						value={props.formData?.username ?? ''}
						onChange={textFieldChanged}
						error={!!props.formErrors?.username}
						helperText={props.formErrors?.username}
					/>
				</Grid>

				<Grid item xs={4}>
					<VisiblePasswordField
						value={props.formData?.password}
						passwordError={props.formErrors?.password}
						textFieldChanged={textFieldChanged}
					/>
				</Grid>

				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						required={true}
						error={!!props.formErrors?.securityMode}>
						<InputLabel>{t('Security Mode')}</InputLabel>
						<Select
							name="securityMode"
							required={true}
							value={props.formData?.securityMode}
							onChange={handleSelectChange}
							error={!!props.formErrors?.securityMode}
							label={t('Security Mode')}>
							{Object.values(props.securityModes).map(
								({ label, value }, key) => (
									<MenuItem key={`sm-${key}`} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.securityMode && (
							<FormHelperText>
								{props.formErrors?.securityMode}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						required={true}
						error={!!props.formErrors?.securityPolicy}>
						<InputLabel>{t('Security Policy')}</InputLabel>
						<Select
							name="securityPolicy"
							required={true}
							value={props.formData?.securityPolicy}
							onChange={handleSelectChange}
							error={!!props.formErrors?.securityPolicy}
							label={t('Security Policy')}>
							{Object.values(props.securityPolicies).map(
								({ label, value }, key) => (
									<MenuItem key={`sp-${key}`} value={value}>
										{label}
									</MenuItem>
								),
							)}
						</Select>
						{!!props.formErrors?.securityPolicy && (
							<FormHelperText>
								{props.formErrors?.securityPolicy}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<FormControl
						variant="outlined"
						margin="normal"
						fullWidth
						required={true}
						error={!!props.formErrors?.endpointMustExist}>
						<InputLabel>{t('Endpoint Must Exist')}</InputLabel>
						<Select
							name="endpointMustExist"
							required={true}
							value={props.formData?.endpointMustExist}
							onChange={handleSelectChange}
							error={!!props.formErrors?.endpointMustExist}
							label={t('Endpoint Must Exist .')}>
							<MenuItem value={'true'}>True</MenuItem>
							<MenuItem value={'false'}>False</MenuItem>
						</Select>
						{!!props.formErrors?.endpointMustExist && (
							<FormHelperText>
								{props.formErrors?.endpointMustExist}
							</FormHelperText>
						)}
					</FormControl>
				</Grid>
				<Grid item xs={6}>
					<TextField
						disabled={false}
						color={'primary'}
						fullWidth
						multiline
						rows={1}
						margin={'normal'}
						label={t('Session Name')}
						required={false}
						variant="outlined"
						onChange={textFieldChanged}
						value={props.formData?.sessionName ?? ''}
						name={'sessionName'}
						error={!!props.formErrors?.sessionName}
						helperText={props.formErrors?.sessionName}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						disabled={true}
						color={'primary'}
						fullWidth
						multiline
						rows={4}
						margin={'normal'}
						label={t('OPC-UA Url')}
						required={false}
						variant="outlined"
						value={props.formData?.opcuaUrl ?? ''}
						name={'opcuaUrl'}
						error={!!props.formErrors?.opcuaUrl}
						helperText={props.formErrors?.opcuaUrl}
						inputProps={{ sx: { fontFamily: 'Monospace' } }}
					/>
				</Grid>

				<Grid item xs={6}>
					<Autocomplete
						id="database-timezone-id"
						options={props.tzOptions}
						getOptionLabel={option => option.value}
						onChange={changeAutocompleteHandler('databaseTimezone')}
						color={'primary'}
						defaultValue={props.tzDefault}
						isOptionEqualToValue={(option, value) =>
							option.value === value.value
						}
						value={{
							key: props.formData?.databaseTimezone,
							value: props.formData?.databaseTimezone?.replace(
								/_/g,
								' ',
							),
						}}
						fullWidth
						renderInput={params => (
							<TextField
								{...params}
								name={'databaseTimezone'}
								label={t('Database Timezone [IANA]')}
								required={true}
								variant="outlined"
								margin={'normal'}
								error={!!props.formErrors?.databaseTimezone}
								helperText={props.formErrors?.databaseTimezone}
							/>
						)}
						onInputChange={textFieldChanged}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Sample Frequency/Duration [Seconds]')}
						required={true}
						variant="outlined"
						name={'sampleDurationSeconds'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 0 } }}
						value={props.formData?.sampleDurationSeconds ?? ''}
						onChange={numberFieldChanged}
						error={!!props.formErrors?.sampleDurationSeconds}
						helperText={props.formErrors?.sampleDurationSeconds}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						color={'primary'}
						fullWidth
						margin={'normal'}
						label={t('Query Group Size')}
						required={true}
						variant="outlined"
						name={'queryGroupSize'}
						type={'number'}
						onWheel={ignoreScroll}
						InputProps={{ inputProps: { min: 1 } }}
						value={props.formData?.queryGroupSize ?? ''}
						onChange={numberFieldChanged}
						error={!!props.formErrors?.queryGroupSize}
						helperText={props.formErrors?.queryGroupSize}
					/>
				</Grid>
			</Grid>
		</StyledForm>
	);
};
